import React, { useState } from 'react';
import {
  Grid,
  List,
  Dialog,
  ListItem,
  DialogContent,
  Button,
  Typography
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import WarningIcon from '@mui/icons-material/Warning';
import SuccessIcon from '@mui/icons-material/CheckCircle';
import { get } from 'lodash';

import { ModalActions } from '@/RestoreComponent/ModalActions';

const useStyles: any = makeStyles(theme => ({
  content: {
    paddingBottom: 6
  },
  statusContainer: {
    width: 554,
    paddingLeft: 4
  },
  status: {
    width: 74,
    margin: 'auto'
  },
  summaryText: {
    textAlign: 'center',
    paddingTop: 16
  },
  error: {
    height: 64,
    width: 64,
    paddingLeft: 15,
    paddingTop: 14,
    backgroundColor: theme.palette.error[50],
    borderRadius: 31,
    '& svg': {
      height: 34,
      width: 34,
      color: theme.palette.error[600]
    }
  },
  warning: {
    height: 64,
    width: 64,
    paddingLeft: 14,
    paddingTop: 13,
    backgroundColor: theme.palette.warning[50],
    borderRadius: 31,
    '& svg': {
      height: 34,
      width: 34,
      color: theme.palette.warning[400]
    }
  },
  success: {
    height: 64,
    width: 64,
    paddingLeft: 14,
    paddingTop: 14,
    backgroundColor: theme.palette.success[50],
    borderRadius: 31,
    '& svg': {
      height: 34,
      width: 34,
      color: theme.palette.success[600]
    }
  },
  errorDisplay: {
    paddingTop: 8,
    textAlign: 'center',
    '& span': {
      paddingTop: 8,
      display: 'block'
    }
  },
  resultLine: {
    display: 'block',
    textAlign: 'center',
    maxHeight: 226
  },
  actions: {
    margin: 'auto !important'
  }
}));

type ResponseModalProps = {
  refundResponses: any[];
  close: (o: boolean) => void;
  isOpen: boolean;
};

interface RefundResponse {
  result: 'success' | 'warn' | 'error';
  details?: string;
  error?: string;
  title?: string;
}

type ResponseStatus = 'success' | 'warn' | 'error';

const getResponseStatus = (responses: RefundResponse[]): ResponseStatus => {
  const warns = responses.some(r => r.result === 'warn');
  const error = responses.some(r => r.result === 'error');
  return !warns && !error ? 'success' : !error ? 'warn' : 'error';
};

export const ResponseModal: React.FC<ResponseModalProps> = ({
  refundResponses,
  isOpen,
  close
}) => {
  const [overallResult] = useState(getResponseStatus(refundResponses));
  const classes = useStyles();
  const firstErrorIndex = refundResponses.findIndex(i => i.result === 'error');
  const firstErrorDescription = get(
    refundResponses,
    `[${firstErrorIndex}].error`
  );
  const firstError = refundResponses[firstErrorIndex];
  return (
    <Dialog open={isOpen} onClose={close}>
      <DialogContent className={classes.content}>
        <Grid container>
          <Grid item>
            <div className={classes.statusContainer}>
              {overallResult === 'success' && (
                <>
                  <div className={classes.status}>
                    <div className={classes.success}>
                      <SuccessIcon />
                    </div>
                  </div>
                  <div className={classes.summaryText}>
                    <Typography variant="h5">Refund Complete</Typography>
                  </div>
                </>
              )}
              {overallResult === 'warn' && (
                <>
                  <div className={classes.status}>
                    <div className={classes.warning}>
                      <WarningIcon />
                    </div>
                  </div>
                  <div className={classes.summaryText}>
                    <Typography variant="h5">Refund Warning</Typography>
                  </div>
                </>
              )}
              {overallResult === 'error' && (
                <>
                  <div className={classes.status}>
                    <div className={classes.error}>
                      {/* prettier-ignore */}
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M11 15h2v2h-2v-2m0-8h2v6h-2V7m1-5C6.47 2 2 6.5 2 12a10 10 0 0 0 10 10a10 10 0 0 0 10-10A10 10 0 0 0 12 2m0 18a8 8 0 0 1-8-8a8 8 0 0 1 8-8a8 8 0 0 1 8 8a8 8 0 0 1-8 8Z"/></svg>
                    </div>
                  </div>
                  <div className={classes.summaryText}>
                    <Typography variant="h5">
                      {firstError.title || 'Refund Error'}
                    </Typography>
                  </div>
                </>
              )}
            </div>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item sm={12}>
            {overallResult !== 'error' && (
              <List>
                {refundResponses.map((r, index) => (
                  <ListItem key={index} className={classes.resultLine}>
                    <Typography variant="body4">{r.details}</Typography>
                  </ListItem>
                ))}
              </List>
            )}
            {overallResult === 'error' && (
              <div className={classes.errorDisplay}>
                <Typography variant="body4">{firstErrorDescription}</Typography>
                <Typography variant="body4">
                  {firstError.details ||
                    'Please contact Restore HQ about this specific refund for assistance.'}
                </Typography>
              </div>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <ModalActions>
        <Button
          className={classes.actions}
          variant="contained"
          color="primary"
          onClick={() => close(true)}
        >
          OK
        </Button>
      </ModalActions>
    </Dialog>
  );
};
